// src/features/links/editCells.tsx
// IR-1047 Phase 2 — inline editors for the two lifecycle columns + Phase 3
// Dismiss action. The selects keep local state seeded from the row so the choice
// shows immediately, then fire onChange; the row prop re-syncs after the
// mutation's list refetch (useEffect).
// IR-1386 r3 — Dismiss also has an icon-only `compact` form for the table.
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { IconButton } from "./IconButton";
import { SETTABLE_OPERATIONAL_STATUSES } from "./api";
import { OP_STATUS_LABELS } from "./labels";
import type { OperationalStatus } from "./types";

interface OperationalStatusCellProps {
  value: OperationalStatus;
  onChange: (next: OperationalStatus) => void;
}

export function OperationalStatusCell({ value, onChange }: OperationalStatusCellProps) {
  const [local, setLocal] = useState<OperationalStatus>(value);
  useEffect(() => setLocal(value), [value]);

  // 'failed' is auto-only (OPS-02) — not in the settable list. If the row is
  // currently 'failed', surface it as a disabled current option so the select
  // still shows the real value without offering it as a manual choice.
  const settable = SETTABLE_OPERATIONAL_STATUSES.includes(local);
  const options: OperationalStatus[] = settable
    ? SETTABLE_OPERATIONAL_STATUSES
    : [local, ...SETTABLE_OPERATIONAL_STATUSES];

  return (
    <select
      aria-label="Operational status"
      // IR-1213: constrain width so the Operational Status column can be
      // minimized. The collapsed value truncates; the full option labels are
      // still shown when the dropdown is open.
      className="iris-input text-xs w-full max-w-[8.5rem] truncate"
      value={local}
      onChange={(e) => {
        const next = e.target.value as OperationalStatus;
        setLocal(next);
        onChange(next);
      }}
    >
      {options.map((s) => (
        <option key={s} value={s} disabled={!SETTABLE_OPERATIONAL_STATUSES.includes(s)}>
          {OP_STATUS_LABELS[s] ?? s}
        </option>
      ))}
    </select>
  );
}

interface ApprovalRequiredCellProps {
  value: boolean;
  onChange: (next: boolean) => void;
}

export function ApprovalRequiredCell({ value, onChange }: ApprovalRequiredCellProps) {
  const [local, setLocal] = useState<boolean>(value);
  useEffect(() => setLocal(value), [value]);

  return (
    <select
      aria-label="Approval required"
      className="iris-input text-xs"
      value={local ? "yes" : "no"}
      onChange={(e) => {
        const next = e.target.value === "yes";
        setLocal(next);
        onChange(next);
      }}
    >
      <option value="no">No</option>
      <option value="yes">Yes</option>
    </select>
  );
}

interface DismissButtonProps {
  onDismiss: () => void;
  /**
   * IR-1386 r3 (Rotem) — icon-only, platform-icon sized, for the table's
   * Actions column. Both states stay two-click: an icon makes a terminal,
   * un-undoable action EASIER to hit by accident, so the confirm step matters
   * more here, not less.
   */
  compact?: boolean;
}

/**
 * IR-1047 Phase 3 (DIS-01) — terminal Dismiss with a two-step inline confirm
 * (no extra dialog). First click arms; second click ("Confirm") fires onDismiss;
 * Cancel disarms. The row leaves the active queue on the post-success refetch.
 */
export function DismissButton({ onDismiss, compact }: DismissButtonProps) {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return compact ? (
      <IconButton icon={X} label="Dismiss link" onClick={() => setArmed(true)} />
    ) : (
      <button
        type="button"
        className="iris-btn text-xs"
        aria-label="Dismiss link"
        onClick={() => setArmed(true)}
      >
        Dismiss
      </button>
    );
  }

  const confirm = () => {
    setArmed(false);
    onDismiss();
  };

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1">
        {/* Tinted, because in icon form this is the only thing distinguishing
            "confirm the terminal action" from "cancel it". */}
        <IconButton icon={Check} label="Confirm dismiss" tone="danger" onClick={confirm} />
        <IconButton icon={X} label="Cancel dismiss" onClick={() => setArmed(false)} />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="iris-btn iris-btn--primary text-xs"
        aria-label="Confirm dismiss"
        onClick={confirm}
      >
        Confirm
      </button>
      <button
        type="button"
        className="iris-btn text-xs"
        aria-label="Cancel dismiss"
        onClick={() => setArmed(false)}
      >
        Cancel
      </button>
    </span>
  );
}
